import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store';
import toast from 'react-hot-toast';

const NAV = [
  { to: '/dashboard',  label: 'Dashboard',     icon: 'dashboard', section: 'main' },
  { to: '/discovery',  label: 'Discovery',     icon: 'search', section: 'main' },
  { to: '/projects',   label: 'Projects',      icon: 'folder', section: 'main' },
  { to: '/assess',     label: 'Assessment',    icon: 'clipboard', section: 'main' },
  { to: '/migration',  label: 'New Migration', icon: 'transfer', section: 'main', badge: null },
  { to: '/users',      label: 'Users',         icon: 'users', section: 'config' },
  { to: '/configuration', label: 'Configuration', icon: 'sliders', section: 'config' },
  { to: '/settings',   label: 'Settings',      icon: 'settings', section: 'config' },
];

const SIDEBAR_EXPANDED = 220;
const SIDEBAR_COLLAPSED = 56;

export default function AppLayout({ children }) {
  
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
    toast.success('Logged out');
    navigate('/login');
  };

  const toggleSidebar = () => setCollapsed(prev => !prev);

  // Close the user dropdown when clicking anywhere outside it.
  useEffect(() => {
    if (!userMenuOpen) return;

    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Header */}
      <header style={{
        height: 'var(--header-h)', flexShrink: 0, zIndex: 100,
        background: 'var(--bg-card)', borderBottom: '0.5px solid var(--border)',
        display: 'flex', alignItems: 'center', padding: '0 20px', gap: 12,
      }}>
        {/* Hamburger */}
        <button
          onClick={toggleSidebar}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{
            width: 32, height: 32, border: 'none', borderRadius: 6,
            background: 'transparent', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text-secondary)', fontSize: 18,
            transition: 'background .15s',
          }}
          onMouseOver={e => { e.currentTarget.style.background = 'var(--bg-secondary)'; }}
          onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src="/logo.png" alt="Logo" style={{ width: 180, marginBottom: 8 }} />
        </div>

        <div style={{ flex: 1 }} />

        {/* Header actions */}
        {/* <NavLink to="/migration">
          <HdrBtn>+ New Migration</HdrBtn>
        </NavLink> */}

        {/* Avatar + dropdown */}
        <div ref={userMenuRef} style={{ position: 'relative' }}>
          <div
            onClick={() => setUserMenuOpen(prev => !prev)}
            title={user?.name || user?.email}
            style={{
              width: 30, height: 30, borderRadius: '50%', cursor: 'pointer',
              background: 'var(--accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 11, fontWeight: 600, userSelect: 'none',
            }}
          >
            {(user?.name || user?.email || '')
              .split(' ')
              .map(n => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2) || 'AU'}
          </div>

          {userMenuOpen && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                minWidth: 200,
                background: 'var(--bg-card)',
                border: '0.5px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-md)',
                zIndex: 200,
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: '12px 14px', borderBottom: '0.5px solid var(--border)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 3 }}>
                  {user?.name || user?.email || 'Unknown User'}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                  {user?.role || 'User'}
                </div>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 14px',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--danger)',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'var(--font-sans)',
                }}
                onMouseOver={e => { e.currentTarget.style.background = 'var(--danger-bg)'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Logout
              </button>
            </div>
          )}
        </div>
      </header>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <nav style={{
          width: sidebarWidth, flexShrink: 0,
          background: 'var(--bg-card)', borderRight: '0.5px solid var(--border)',
          display: 'flex', flexDirection: 'column', overflowY: 'auto',
          overflowX: 'hidden',
          transition: 'width .2s ease',
        }}>
          {!collapsed && <SideSection label="Main" />}
          {NAV.filter(n => n.section === 'main').map(n =>
            <SideItem key={n.to} {...n} collapsed={collapsed} />
          )}

          {!collapsed && <SideSection label="Config" />}
          {collapsed && <div style={{ marginTop: 12 }} />}
          {NAV.filter(n => n.section === 'config').map(n =>
            <SideItem key={n.to} {...n} collapsed={collapsed} />
          )}

          {/* Bottom status */}
          <div style={{ marginTop: 'auto', padding: collapsed ? 8 : 12, borderTop: '0.5px solid var(--border)' }}>
            {collapsed ? (
              <div style={{
                width: 32, height: 32, margin: '0 auto',
                borderRadius: '50%', background: 'var(--bg-secondary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--success)', display: 'block' }} />
              </div>
            ) : (
              <div style={{
                padding: '10px 12px', background: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-md)', fontSize: 12,
              }}>
                <div style={{ fontWeight: 500, color: 'var(--text-primary)', marginBottom: 3 }}>
                  <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: 'var(--success)', marginRight: 5 }} />
                  Copyright &copy; Bloomfieldx
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Main content */}
        <main style={{ flex: 1, overflowY: 'auto', padding: location?.pathname === "/assess" || location?.pathname === "/discovery" || location?.pathname.startsWith("/projects/") || location?.pathname === "/projects" ? 0 : 0 }}>
          {children}
        </main>
      </div>
    </div>
  );
}

function SideSection({ label }) {
  return (
    <div style={{
      padding: '12px 8px 4px', fontSize: 10, fontWeight: 500,
      color: 'var(--text-tertiary)', textTransform: 'uppercase',
      letterSpacing: '.06em', marginTop: 4,
    }}>
      {label}
    </div>
  );
}

function SideItem({ to, label, icon, badge, collapsed }) {
  return (
    <NavLink to={to} style={{ textDecoration: 'none' }}>
      {({ isActive }) => (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 8,
            padding: collapsed ? '10px 0' : '8px 12px',
            margin: collapsed ? '1px 4px' : '1px 6px',
            borderRadius: 'var(--radius-md)',
            justifyContent: collapsed ? 'center' : 'flex-start',
            color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
            fontWeight: isActive ? 500 : 400, fontSize: 13,
            background: isActive ? 'var(--accent-light)' : 'transparent',
            cursor: 'pointer', transition: 'all .15s',
            position: 'relative',
          }}
          title={collapsed ? label : undefined}
          onMouseOver={e => { if (!isActive) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
          onMouseOut={e  => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
        >
          {isActive && (
            <div style={{
              position: 'absolute', left: 0, top: '25%', bottom: '25%',
              width: 2.5, background: 'var(--accent)', borderRadius: 2,
            }} />
          )}
          <span style={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <NavIcon name={icon} />
          </span>
          {!collapsed && <span>{label}</span>}
          {!collapsed && badge && (
            <span style={{
              marginLeft: 'auto', fontSize: 10, padding: '1px 6px',
              borderRadius: 10, background: 'var(--danger-bg)', color: 'var(--danger-text)', fontWeight: 500,
            }}>{badge}</span>
          )}
        </div>
      )}
    </NavLink>
  );
}

function NavIcon({ name }) {
  const props = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };

  switch (name) {
    case 'dashboard':
      return (
        <svg {...props}>
          <rect x="3" y="3" width="7" height="9" rx="1" />
          <rect x="14" y="3" width="7" height="5" rx="1" />
          <rect x="14" y="12" width="7" height="9" rx="1" />
          <rect x="3" y="16" width="7" height="5" rx="1" />
        </svg>
      );
    case 'search':
      return (
        <svg {...props}>
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      );
    case 'clipboard':
      return (
        <svg {...props}>
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
          <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
          <path d="M9 14l2 2 4-4" />
        </svg>
      );
    case 'folder':
      return (
        <svg {...props}>
          <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
        </svg>
      );
    case 'transfer':
      return (
        <svg {...props}>
          <polyline points="17 1 21 5 17 9" />
          <line x1="3" y1="5" x2="21" y2="5" />
          <polyline points="7 23 3 19 7 15" />
          <line x1="21" y1="19" x2="3" y2="19" />
        </svg>
      );
    case 'users':
      return (
        <svg {...props}>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'settings':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1.08 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1.08z" />
        </svg>
      );
    case 'sliders':
      return (
        <svg {...props}>
          <line x1="4" y1="21" x2="4" y2="14" />
          <line x1="4" y1="10" x2="4" y2="3" />
          <line x1="12" y1="21" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12" y2="3" />
          <line x1="20" y1="21" x2="20" y2="16" />
          <line x1="20" y1="12" x2="20" y2="3" />
          <line x1="1" y1="14" x2="7" y2="14" />
          <line x1="9" y1="8" x2="15" y2="8" />
          <line x1="17" y1="16" x2="23" y2="16" />
        </svg>
      );
    default:
      return <span style={{ fontSize: 16 }}>*</span>;
  }
}

function HdrBtn({ children, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 5,
      padding: '5px 12px', border: '0.5px solid var(--border-med)',
      borderRadius: 'var(--radius-md)', background: 'transparent',
      color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12,
      fontFamily: 'var(--font-sans)', transition: 'all .15s',
    }}
    onMouseOver={e => { e.currentTarget.style.background = 'var(--bg-secondary)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
    onMouseOut={e  => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
    >
      {children}
    </button>
  );
}
